import { z } from "zod";
import { IsoTimestamp, NightId } from "./ids.js";

// Night lifecycle:
//   SCHEDULED ── night_opened ──▶ OPEN        (feed live, push fires, hero prefetch)
//   SCHEDULED ── night_cancelled ▶ CANCELLED
//   OPEN ─────── night_closing ─▶ CLOSING     (no new matches; in-flight finish)
//   CLOSING ──── night_closed ──▶ CLOSED      (DUGOUT typed-confirm close; settlement sweep)
export const NightState = z.enum(["SCHEDULED", "OPEN", "CLOSING", "CLOSED", "CANCELLED"]);
export type NightState = z.infer<typeof NightState>;

export const NIGHT_TERMINAL_STATES: readonly NightState[] = ["CLOSED", "CANCELLED"];

export const PrizeStructure = z.object({
  tiers: z.array(
    z.object({
      rank_from: z.number().int().min(1),
      rank_to: z.number().int().min(1),
      payout_type: z.string(), // reason-coded; resolves against voucher_pools/coins
      payout_value: z.number().int().min(0),
    }),
  ),
});
export type PrizeStructure = z.infer<typeof PrizeStructure>;

export const NightConfig = z.object({
  featured_moment: z.string().nullable(),
  prize_structure: PrizeStructure,
  hero_asset: z.string().nullable(), // prefetched at night-open push time, never at render
});
export type NightConfig = z.infer<typeof NightConfig>;

export const Night = z.object({
  id: NightId,
  code: z.string().min(1), // e.g. "N-2026-07-23"
  state: NightState,
  config: NightConfig,
  scheduled_for: IsoTimestamp,
  opened_at: IsoTimestamp.nullable(),
  closed_at: IsoTimestamp.nullable(),
  /** When the standings snapshot last advanced (0011). Null until first ticked.
   *  Gates the sweep so a rank change stays VISIBLE for a full window instead
   *  of being overwritten on the next 30s pass — see STANDINGS_TICK_WINDOW_MS. */
  standings_ticked_at: IsoTimestamp.nullable().default(null),
});
export type Night = z.infer<typeof Night>;
